import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Copy, Award, Trophy, Sparkles, BookOpen, GraduationCap, Pill, Shirt, ExternalLink, TrendingUp, Repeat, Lock, Gift } from "lucide-react";
import Brand, { GoliMark } from "../Brand";

const G    = "#2E9F3A";
const O    = "#F39A1F";
const WINE = "#0F172A";

const CATEGORY_TABS = [
  { id: "books",        label: "My Books",     icon: BookOpen,       blurb: <span>Faitharama titles + author books published on the platform.</span> },
  { id: "courses",      label: "Courses",      icon: GraduationCap,  blurb: <span>Every course is sellable to non-members at retail.</span> },
  { id: "supplements",  label: "Supplements",  icon: Pill,           blurb: <span><GoliMark/> · Easy to swallow · 26% OFF · Earn 20% commission.</span> },
  { id: "merchandise",  label: "Merchandise",  icon: Shirt,          blurb: <span>Tees, mugs, journals · Printful-fulfilled.</span> },
];

export default function AffiliatesTab() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState(null);
  const [activeTab, setActiveTab] = useState("books");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/affiliate/stats").then(r => r.data),
      api.get("/affiliate/products").then(r => r.data),
    ]).then(([s, p]) => {
      setStats(s);
      setProducts(p);
    }).catch(() => toast.error("Could not load affiliate data"))
      .finally(() => setLoading(false));
  }, []);

  const copy = (text, label = "Link copied") => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(label),
      () => toast.error("Could not copy — select and copy manually.")
    );
  };

  const items = useMemo(() => products?.categories?.[activeTab] || [], [products, activeTab]);
  const counts = products?.category_counts || {};

  if (loading) return <div className="text-foreground/60 text-center py-16" data-testid="affiliates-loading">Loading your affiliate dashboard…</div>;
  if (!stats) return <div className="text-foreground/60 text-center py-16">Could not load stats.</div>;

  const isPaid = stats.is_paid_member;
  const progressPct = stats.is_paid_member && stats.tier_key !== "paid_top"
    ? Math.min(100, (stats.lifetime_sales_usd / stats.next_threshold_usd) * 100)
    : 100;

  return (
    <div className="space-y-8" data-testid="affiliates-tab">
      {/* ============== HERO ============== */}
      <div className="rounded-2xl bg-gradient-to-br from-[#0F172A] via-[#2A2521] to-[#0F172A] text-white p-7 md:p-10 shadow-2xl border-2 border-[#F39A1F]/40 relative overflow-hidden" data-testid="affiliate-hero">
        <div className="absolute top-3 right-3 bg-[#F39A1F] text-[#0F172A] text-[10px] font-bold uppercase tracking-wider-cap px-3 py-1 rounded-full" data-testid="affiliate-tier-badge">{stats.tier_label}</div>
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-5 h-5 text-[#F39A1F]"/>
          <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F]">★ <Brand/> AFFILIATE PROGRAM</div>
        </div>
        <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-tight">Earn 50% Every Month. Forever.</h2>
        <p className="text-white/85 mt-2 max-w-2xl text-base">
          Share <Brand/> with one friend.{" "}
          <strong className="text-[#F39A1F]">You earn 50% of their plan EVERY MONTH</strong> — for as long as they stay.
          That&apos;s <strong className="text-[#F39A1F]">$24.50/mo</strong> on every Pro you refer,{" "}
          <strong className="text-[#F39A1F]">$39.50/mo</strong> on every Unlimited. Refer 10 Pro members → that&apos;s{" "}
          <strong className="text-[#F39A1F]">$245/mo recurring</strong> coming back to you.
        </p>

        <div className="grid md:grid-cols-[1fr,auto] gap-4 mt-6 items-end">
          <div>
            <div className="text-[10px] tracking-wider-cap text-white/60 mb-1">Your Affiliate Link</div>
            <div className="flex flex-wrap items-stretch gap-2">
              <div className="font-serif-display text-2xl text-[#F39A1F] mr-3" data-testid="affiliate-code">{stats.code}</div>
              <input
                readOnly value={stats.share_link}
                className="flex-1 min-w-[260px] px-3 py-2.5 rounded-md bg-white/95 text-[#0F172A] text-sm font-mono border-0"
                data-testid="affiliate-link"
                onFocus={e => e.target.select()}
              />
              <button onClick={() => copy(stats.share_link, "★ Affiliate link copied!")} className="bg-[#F39A1F] text-[#0F172A] px-4 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs hover:bg-white transition flex items-center gap-1.5" data-testid="copy-affiliate-link">
                <Copy className="w-3.5 h-3.5"/> Copy
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ============== YOUR FREE GIVEAWAY — lead-magnet block for every tier ============== */}
      <div className="rounded-2xl p-5 md:p-6 shadow-md flex flex-col md:flex-row md:items-center gap-4 bg-white"
           style={{ border: `2px solid #2E9F3A` }}
           data-testid="affiliate-giveaway-block">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow text-white"
             style={{ background: "#2E9F3A" }}>
          <Gift className="w-7 h-7"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: "#2E9F3A" }}>
            ★ YOUR FREE GIVEAWAY · BUNDLED FOR YOU
          </div>
          <h3 className="font-serif-display text-xl md:text-3xl uppercase leading-tight mt-0.5" style={{ color: WINE }}>
            Free Faitharama Guide + 7-Day Free Trial
          </h3>
          <p className="text-sm md:text-base text-foreground/85 mt-1 leading-relaxed">
            Share this link — visitors get the free <Brand suffix=".com"/> guide AND a 7-day free trial. <strong>Flat 50/50 split</strong> on every paid upgrade, forever.
          </p>
          <div className="mt-3 flex flex-wrap items-stretch gap-2">
            <input
              readOnly
              value={`${window.location.origin}/free-book?ref=${stats.code}`}
              data-testid="affiliate-giveaway-link"
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-[240px] px-3 py-2.5 rounded-md bg-white text-[#0F172A] text-xs md:text-sm font-mono border-2"
              style={{ borderColor: `#2E9F3A55` }}
            />
            <button onClick={() => copy(`${window.location.origin}/free-book?ref=${stats.code}`, "★ Giveaway link copied!")}
                    data-testid="affiliate-giveaway-copy"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md font-bold uppercase tracking-[0.16em] text-xs hover:scale-[1.03] transition"
                    style={{ background: G, color: "white" }}>
              <Copy className="w-3.5 h-3.5"/> Copy Link
            </button>
            <a href="/free-gift/30-day-challenge" target="_blank" rel="noreferrer"
               data-testid="affiliate-giveaway-preview"
               className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md font-bold uppercase tracking-[0.16em] text-xs hover:scale-[1.03] transition"
               style={{ background: "#FFFFFF", color: WINE, border: `1.5px solid ${WINE}` }}>
              Preview Page
            </a>
          </div>
        </div>
      </div>

      {/* ============== STATS STRIP ============== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="affiliate-stats-strip">
        <StatCard label="Referrals" value={stats.stats.referrals} icon={TrendingUp} testid="stat-referrals"/>
        <StatCard label="Active Recurring" value={stats.stats.active_recurring} icon={Repeat} testid="stat-recurring"/>
        <StatCard label="Recurring / Mo" value={`$${stats.stats.monthly_recurring_earnings_usd.toFixed(2)}`} icon={Sparkles} testid="stat-monthly"/>
        <StatCard label="Lifetime Sales" value={`$${stats.lifetime_sales_usd.toFixed(2)}`} icon={Award} testid="stat-lifetime"/>
      </div>

      {/* ============== ONE FLAT RATE — NO LADDER ============== */}
      <div className="rounded-2xl border-2 p-6 md:p-8 shadow-lg" style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #EAF6FC 100%)", borderColor: "#2E9F3A" }} data-testid="affiliate-flat-rate">
        <div className="grid md:grid-cols-[auto_1fr_auto] gap-5 items-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow text-white" style={{ background: "#2E9F3A" }}>
            <Trophy className="w-8 h-8" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: "#2E9F3A" }}>★ YOUR FLAT RATE</div>
            <h3 className="font-serif-display text-3xl md:text-4xl uppercase leading-tight mt-1" style={{ color: "#0F172A" }}>
              50% Of Every Sale. Forever.
            </h3>
            <p className="text-sm md:text-base mt-1 leading-snug" style={{ color: "#0F172A" }}>
              Every <Brand suffix=".com"/> member — free trial, Pro, Unlimited — gets a flat 50/50 split. No tiers. No hoops.
            </p>
          </div>
          {!isPaid && (
            <Link to="/pricing?plan=faithful_monthly" className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-bold uppercase tracking-[0.16em] text-xs text-white shadow whitespace-nowrap" style={{ background: "#2E9F3A" }} data-testid="ladder-upgrade-btn">
              Join For $49 →
            </Link>
          )}
        </div>
      </div>

      {/* ============== HOW THE COMMISSION ACTUALLY WORKS ============== */}
      <div className="rounded-2xl border-2 p-6 md:p-8 bg-white" style={{ borderColor: "#2E9F3A", background: "linear-gradient(135deg, #FFFFFF 0%, #EAF6FC 100%)" }} data-testid="recurring-math">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-2" style={{ color: "#2E9F3A" }}>★ HOW YOU GET PAID</div>
        <h3 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight" style={{ color: "#0F172A" }}>
          $49+ Qualifies You For Everything <Brand suffix=".com"/> Offers.
        </h3>
        <p className="text-foreground text-base md:text-lg leading-relaxed mt-3">
          Plain English: <strong>any paid member from the $49 Pro up</strong> qualifies for every <Brand /> affiliate stream — books, courses, supplements, merch, all of it.
        </p>
        <p className="text-foreground text-base md:text-lg leading-relaxed mt-3">
          <strong>The only difference between plans is how much of each thing you keep.</strong> The higher your plan, the bigger your cut on every sale. Same access, bigger paycheck.
        </p>
        <p className="text-foreground text-base md:text-lg leading-relaxed mt-3">
          <strong>How the math works:</strong> if a partner brand pays <Brand /> 10% on a sale, you keep <strong style={{ color: "#2E9F3A" }}>60% of that 10%</strong>. No fixed-dollar promises &mdash; your cut scales with what we actually collect.
        </p>
        <p className="text-foreground text-base md:text-lg leading-relaxed mt-3">
          <strong>Want even more?</strong> Apply direct with the brand yourself (Goli, Piurify, Vanotium, etc) and use YOUR own affiliate link instead of ours. Most brands pay 10–20% to new affiliates — you keep 100% of whatever they pay you.
        </p>
      </div>

      {/* ============== CATEGORY TABS ============== */}
      <div data-testid="category-tabs">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {CATEGORY_TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            const count = counts[tab.id] ?? 0;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`px-3 py-3 rounded-md border-2 text-xs uppercase tracking-wider-cap font-bold transition flex flex-col items-center gap-1.5 ${
                  active
                    ? "bg-[#2E9F3A] border-[#2E9F3A] text-white shadow-lg"
                    : "bg-white border-foreground/15 text-foreground hover:border-[#2E9F3A]/40 hover:bg-white/40"
                }`}>
                <Icon className="w-4 h-4"/>
                {tab.label}
                <span className={`text-[9px] tracking-wider-cap ${active ? "text-white/80" : "text-foreground/55"}`}>{count} {count === 1 ? "ITEM" : "ITEMS"}</span>
              </button>
            );
          })}
        </div>

        <div className="rounded-2xl bg-white border border-[#F39A1F]/20 p-5">
          <p className="text-sm text-foreground/85 italic mb-4">{CATEGORY_TABS.find(t => t.id === activeTab)?.blurb}</p>

          {items.length === 0 ? (
            <div className="text-center py-12 text-foreground/55" data-testid={`${activeTab}-empty`}>
              <div className="font-serif-display text-2xl text-red-wine/60 uppercase mb-1">Nothing Here Yet</div>
              <p className="text-sm">{activeTab === "books" ? "Member-published books appear here once Mike approves them on the Books page." : "Items will appear when added to your market."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${activeTab}-grid`}>
              {items.map(item => (
                <ProductCard key={item.id} item={item} onCopy={copy} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============== sub-components ============== */
function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div className="faith-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] tracking-wider-cap font-bold text-gold-deep mb-1">
        <Icon className="w-3.5 h-3.5"/>
        {label}
      </div>
      <div className="font-serif-display text-2xl md:text-3xl text-red-wine">{value}</div>
    </div>
  );
}

function LadderRung({ rung, index }) {
  const isActive = rung.active;
  const isGold = rung.tier_key === "paid_top";
  const RUNG_COLORS = {
    free:          { bg: "from-[#FFFFFF] to-[#FFFFFF]",  border: "#CD7F32",  text: "text-foreground",  badge: "bg-[#CD7F32] text-white",   trophy: "text-[#CD7F32]" },
    paid_baseline: { bg: "from-white to-[#F8F8F8]",      border: "#C0C0C0",  text: "text-foreground",  badge: "bg-[#7A7A7A] text-white",   trophy: "text-[#7A7A7A]" },
    paid_top:      { bg: "from-white to-[#FBE9A0]",  border: "#F39A1F",  text: "text-[#0F172A]",   badge: "bg-[#0F172A] text-[#F39A1F]", trophy: "text-[#F39A1F]" },
  }[rung.tier_key];

  return (
    <div
      data-testid={`ladder-rung-${rung.tier_key}`}
      className={`rounded-xl bg-gradient-to-br ${RUNG_COLORS.bg} ${RUNG_COLORS.text} p-5 transition relative ${isActive ? "ring-4 ring-offset-2 ring-[#2E9F3A] shadow-2xl scale-[1.02]" : "border-2 hover:scale-[1.01]"}`}
      style={{borderColor: isActive ? "#2E9F3A" : RUNG_COLORS.border}}>
      <div className="flex items-start justify-between mb-2">
        <Trophy className={`w-7 h-7 ${RUNG_COLORS.trophy}`}/>
        {isActive && <span className="bg-[#2E9F3A] text-white text-[9px] uppercase tracking-wider-cap font-bold px-2 py-0.5 rounded-full" data-testid={`active-badge-${rung.tier_key}`}>★ YOU ARE HERE</span>}
      </div>
      <div className={`text-[9px] tracking-wider-cap font-bold ${RUNG_COLORS.trophy} mb-0.5`}>STEP {index + 1}</div>
      <div className="font-serif-display text-2xl uppercase leading-tight">{rung.label}</div>
      <div className={`inline-block mt-2 px-3 py-1 rounded-full text-xs uppercase tracking-wider-cap font-bold ${RUNG_COLORS.badge}`}>{rung.pct}% Commission</div>
      <p className="text-sm mt-2 leading-snug">{rung.blurb}</p>
    </div>
  );
}

function ProductCard({ item, onCopy }) {
  return (
    <div className="bg-white rounded-xl overflow-hidden border-2 border-foreground/10 hover:border-[#F39A1F] hover:shadow-xl transition flex flex-col" data-testid={`product-${item.id}`}>
      <div className="aspect-[16/10] bg-foreground/10 relative overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#F39A1F]"><BookOpen className="w-10 h-10"/></div>
        )}
        {item.badge && <div className="absolute top-2 left-2 bg-[#0F172A]/85 backdrop-blur-sm text-[#F39A1F] text-[9px] tracking-wider-cap font-bold px-2 py-0.5 rounded-full">{item.badge}</div>}
        <div className="absolute bottom-2 right-2 bg-[#F39A1F] text-[#0F172A] text-xs font-bold px-2.5 py-0.5 rounded-full" data-testid={`price-${item.id}`}>${item.price_usd.toFixed(2)}</div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-bold text-red-wine text-base leading-tight line-clamp-2 mb-1">{item.title}</div>
        {item.subtitle && <div className="text-xs text-foreground/70 italic line-clamp-2 mb-2">{item.subtitle}</div>}

        <div className="mt-auto pt-2 border-t border-[#F39A1F]/20">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] tracking-wider-cap font-bold text-foreground/60">YOU EARN</div>
            <div className="font-serif-display text-2xl text-red-wine">${item.commission_per_sale.toFixed(2)} <span className="text-xs uppercase tracking-wider-cap text-gold-deep">· {item.your_pct}%</span></div>
          </div>
          <button
            onClick={() => onCopy(item.share_link, `★ ${item.title.slice(0, 30)}… link copied`)}
            data-testid={`copy-${item.id}`}
            className="w-full bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-3 py-2 rounded-md font-bold uppercase tracking-wider-cap text-[11px] transition flex items-center justify-center gap-1.5">
            <Copy className="w-3.5 h-3.5"/> Copy My Sales Link
          </button>
          <a href={item.share_link} target="_blank" rel="noopener noreferrer" className="block text-center text-[10px] uppercase tracking-wider-cap font-bold text-foreground/60 hover:text-red-wine mt-1.5" data-testid={`preview-${item.id}`}>Preview Sales Page <ExternalLink className="w-3 h-3 inline-block"/></a>
        </div>
      </div>
    </div>
  );
}
